import { useEffect, useMemo } from "react";
import {
  newTracker,
  trackPageView,
  trackStructEvent,
  addGlobalContexts,
  removeGlobalContexts,
  setUserId,
  enableActivityTracking,
} from "@snowplow/browser-tracker";
import { LinkClickTrackingPlugin, enableLinkClickTracking } from "@snowplow/browser-plugin-link-click-tracking";
import { ButtonClickTrackingPlugin, enableButtonClickTracking } from "@snowplow/browser-plugin-button-click-tracking";
import { FormTrackingPlugin, enableFormTracking } from "@snowplow/browser-plugin-form-tracking";
import { useLocation } from "react-router";
import type { UserInfoOrganisation } from "../api/userInfo";

const R12_CODE = "234";
const SERVICE_NAME = "Dataskydd";
const APP_ID = "draftit";
const TRACKER_ID = "cf";
const COLLECTOR_URL = "https://snowplow.visma.com";

let initialized = false;

const initSnowplow = (collector: string) => {
  if (initialized) return;
  newTracker(TRACKER_ID, collector, {
    appId: APP_ID,
    contexts: { webPage: true, session: true, browser: true },
    plugins: [LinkClickTrackingPlugin(), ButtonClickTrackingPlugin(), FormTrackingPlugin()],
  });
  enableLinkClickTracking({ pseudoClicks: true, trackContent: true });
  enableButtonClickTracking();
  enableFormTracking({
    options: { events: ["focus_form", "submit_form"], fields: { transform: () => "redacted" } },
  });
  enableActivityTracking({ minimumVisitLength: 30, heartbeatDelay: 20 });
  initialized = true;
};

type SnowplowProviderProps = {
  organisation: UserInfoOrganisation;
  user: { createdAt: number; email: string | null };
};

export const InitSnowplow = ({ organisation, user }: SnowplowProviderProps) => {
  useEffect(() => {
    initSnowplow(COLLECTOR_URL);
  }, []);

  const contexts = useMemo(
    () => [
      {
        schema: "iglu:com.visma-tech-hubs/context_customer/jsonschema/1-0-0",
        data: { customer_name: organisation.name, business_unit: "Draftit", countryiso: "SE" },
      },
      {
        schema: "iglu:com.visma-tech-hubs/context_product/jsonschema/1-0-0",
        data: { r12: R12_CODE, product_name: SERVICE_NAME },
      },
      {
        schema: "iglu:com.visma-tech-hubs/context_user/jsonschema/1-0-0",
        data: { user_type: "Customer", registration_date: new Date(user.createdAt * 1000).toISOString().slice(0, 10) },
      },
      {
        schema: "iglu:com.visma-tech-hubs/context_deletion/jsonschema/1-0-0",
        data: { deletion_id: "1" },
      },
      {
        schema: "iglu:com.visma-tech-hubs/context_custom/jsonschema/1-0-0",
        data: { custom_name: "", se_category: "", se_action: "", se_label: "", se_property: "" },
      },
    ],
    [organisation.name, user.createdAt]
  );

  useEffect(() => {
    if (!initialized) return;
    removeGlobalContexts(contexts);
    addGlobalContexts(contexts);
    return () => removeGlobalContexts(contexts);
  }, [contexts]);

  useEffect(() => {
    if (!initialized) return;
    setUserId(user.email);
  }, [user.email]);

  const location = useLocation();
  useEffect(() => {
    if (!initialized) return;
    trackPageView({ title: document.title });
  }, [location.pathname, location.search, location.hash]);

  return null;
};

type EventCategory =
  | "registration"
  | "dynamic-grid"
  | "dynamic-form"
  | "data-import"
  | "system"
  | "partner"
  | "primary-assets"
  | "hubspot";
const customEvent = (category: EventCategory, action: string, property?: string) =>
  trackStructEvent({ category, action, property });

export const trackEvent = {
  createRegistrationClick: () => customEvent("registration", "Create registration"),
  copyRegistrationClick: () => customEvent("registration", "Copy registration"),
  changeRegistrationSavedFilters: () => customEvent("registration", "Change registration saved filters"),
  clickRegistrationHistoryTab: () => customEvent("registration", "Navigate to history"),
  clickRegistrationGuidedTab: () => customEvent("registration", "Navigate to guided view"),
  answerQuestionRegistrationGuidedView: (inputType: string) =>
    customEvent("registration", "Answer question in guided view:", inputType),
  inviteeRegistrationClick: () => customEvent("registration", "Add invitee"),
  navigationCellClickWithinRegistrationGrid: (cellType: string) =>
    customEvent("registration", "Registration link click within registration grid", cellType),
  createNewOptionFromForm: (inputType: string) => customEvent("dynamic-form", "Create new option from form", inputType),

  exportExcelClick: () => customEvent("dynamic-grid", "Export Excel"),
  exportCSVClick: () => customEvent("dynamic-grid", "Export CSV"),
  exportPDFClick: (formType: string) => customEvent("dynamic-grid", "Export PDF", formType),
  clickSidePanelTab: (tab: string) => customEvent("dynamic-grid", "Click side panel tab", tab),

  clickSystemHistoryTab: () => customEvent("system", "Navigate to history"),
  clickDataImportDownloadFile: (fileType: string) => customEvent("data-import", "Click download file", fileType),

  clickPartnerSecurityAndPrivacyProfileUpgradeLink: (organisation: string, hubspotId: string | null | undefined) => {
    // Send the partner event
    customEvent(
      "partner",
      "Click partner security and privacy profile upgrade link",
      `Organisation name: ${organisation}`
    );
    // Send a separate hubspot event
    customEvent("hubspot", "Click partner security and privacy profile upgrade link", hubspotId ?? "");
  },
  clickComplianceSubDrawer: () => customEvent("partner", "Click compliance sub drawer"),
  clickVendorAssessmentPartnerDrawerWithoutFeatureEnabled: () =>
    customEvent("partner", "Vendor assessment button in Partner drawer clicked without feature enabled"),
  clickPrimaryAssetsUpsellRequestDemo: () =>
    customEvent("primary-assets", "Primary assets request demo button clicked"),
  clickPrimaryAssetsSidebarItemWithoutFeatureEnabled: () =>
    customEvent("primary-assets", "Primary assets sidebar item clicked without feature enabled"),
  archivePartner: (partnerName: string, organisation: string) =>
    customEvent("partner", "Archived from replace and archive dialog", `${partnerName} - ${organisation}`),
};
